import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import {
  atom,
  useAtom,
  useAtomValue,
  useSetAtom,
} from 'infrastructure/state/jotai.wrapper'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material'
import { Atom } from 'infrastructure/state/jotai.wrapper'
import { loadable } from 'jotai/utils'

export const globalDialogAtom = atom<
  { title: ReactNode; contents: ReactNode[] } | undefined
>(undefined)

export function GlobalDialog() {
  const [dialogData, setDialogData] = useAtom(globalDialogAtom)
  if (!dialogData?.contents.length) return null
  const onClose = () => setDialogData(undefined)
  return (
    <Dialog open={true} maxWidth="xl" fullWidth={true} onClose={onClose}>
      <DialogTitle>{dialogData.title}</DialogTitle>
      <DialogContent>
        {dialogData.contents.map((node, index) => (
          <Box key={index} paddingBottom="8px">
            {node}
          </Box>
        ))}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="primary" onClick={onClose}>
          Dismiss
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export interface WithErrorResult<T> {
  result?: T
  error?: string | string[]
  errorDetails?: string
}

export function useWithErrorNotification<T, V extends unknown[]>(
  { errorTitle }: { errorTitle: string },
  op: (...args: V) => Promise<WithErrorResult<T>>,
  errNotif: (args: WithErrorResult<T>) => string[] = ({ error }) => {
    if (error) {
      if (Array.isArray(error)) return error
      return [error]
    }
    return []
  },
) {
  const setDialog = useSetAtom(globalDialogAtom)
  return useCallback(
    async (...args: V) => {
      const { result, ...rest } = await op(...args)
      const error = errNotif(rest)
      if (error.length) {
        setDialog({
          title: errorTitle,
          contents: error,
        })
      }
      return result
    },
    [op, errorTitle, setDialog, errNotif],
  )
}

export function useWithErrorNotificationStatus<T, V>(
  {
    errorTitle,
    successMessage,
    successComponent,
  }: {
    errorTitle: string
    successMessage?: string
    successComponent?: (r: T) => ReactNode
  },
  op: (...args: V[]) => Promise<WithErrorResult<T>>,
) {
  const setDialog = useSetAtom(globalDialogAtom)
  return useCallback(
    async (...args: V[]) => {
      const { error, result } = await op(...args)
      if (error) {
        setDialog({ title: errorTitle, contents: [error] })
        return false
      }
      const content: ReactNode[] = []
      if (successMessage) {
        content.push(successMessage)
      }
      if (result && successComponent) {
        content.push(successComponent(result))
      }
      if (content.length) {
        setDialog({ title: 'Update Success', contents: content })
      }
      return true
    },
    [op, errorTitle, setDialog, successMessage, successComponent],
  )
}

export function useAtomWithErrorNotification<T>(
  { errorTitle }: { errorTitle: string },
  atom: Atom<WithErrorResult<T> | Promise<WithErrorResult<T>>>,
) {
  const errorSetter = useSetAtom(globalDialogAtom)
  const loadableAtom = useMemo(() => loadable(atom), [atom])
  const load = useAtomValue(loadableAtom)
  const lastErrorRef = useRef<string | null>(null)

  const { state } = load

  let error: string | null = null
  if (state === 'hasError') {
    error =
      typeof load.error === 'object'
        ? JSON.stringify(load.error, null, 2)
        : String(load.error)
  } else if (state === 'hasData') {
    error = (load.data.error && String(load.data.error)) ?? null
  }

  useEffect(() => {
    if (error && error !== lastErrorRef.current) {
      lastErrorRef.current = error
      errorSetter({ title: errorTitle, contents: [String(error)] })
    }
  }, [state, error, errorSetter, errorTitle])

  return load.state === 'hasData' ? load.data.result : undefined
}

export function useAdminQueryWithErrorNotification<T, V extends unknown[]>(
  { errorTitle }: { errorTitle: string },
  op: (...args: V) => Promise<{ data: T; errors?: { message: string }[] }>,
) {
  const setDialog = useSetAtom(globalDialogAtom)
  return useCallback(
    async (...args: V) => {
      const { data, errors } = await op(...args)
      if (errors?.length) {
        setDialog({
          title: errorTitle,
          contents: errors.map(({ message }) => message),
        })
      }
      return data
    },
    [op, errorTitle, setDialog],
  )
}
